import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getPolicyContent, PolicyContent } from '../services/settingsService';

interface PolicyDocumentProps {
  type: 'terms' | 'privacy';
}

/**
 * Renders a policy document from the database.
 *
 * The Privacy Notice and Website Terms live in ApplicationSettings and are served by
 * /api/settings/policy/{type}. They are deliberately NOT duplicated in the front end.
 * Until 21 Aug 2026 there were three copies — the database, a fallback inside PolicyModal,
 * and the /privacy and /terms pages — and they had drifted apart: the pages still named
 * autoaccesssolutions.com and talked about shipping orders, while the signup consent modal
 * showed something else entirely. For a document with an effective date on it, one source
 * is the only workable arrangement.
 *
 * If the content cannot be loaded, this says so and gives the reader somewhere to go. It
 * does not fall back to a bundled copy, because a stale policy presented as the current one
 * is worse than an honest "temporarily unavailable".
 */
const PolicyDocument: React.FC<PolicyDocumentProps> = ({ type }) => {
  const [policy, setPolicy] = useState<PolicyContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    getPolicyContent(type)
      .then((content) => {
        if (!cancelled) setPolicy(content);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // The reader can navigate away mid-request; without this the state update lands on an
    // unmounted component.
    return () => {
      cancelled = true;
    };
  }, [type]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16" role="status" aria-live="polite">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (!policy?.content) {
    return (
      <div className="text-slate-300 leading-relaxed">
        <p className="mb-4">
          This document is temporarily unavailable. Please try again shortly.
        </p>
        <p className="mb-0">
          For a copy in the meantime, contact AutoAccess Solutions on{' '}
          <a href="tel:+15025430877" className="text-white hover:text-gray-300 transition-colors">
            (502) 543-0877
          </a>{' '}
          or{' '}
          <a
            href="mailto:contact@autoaccesssolutions.org"
            className="text-white hover:text-gray-300 transition-colors"
          >
            contact@autoaccesssolutions.org
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <>
      <div dangerouslySetInnerHTML={{ __html: policy.content }} />
      {policy.lastUpdated && (
        <p className="text-sm text-slate-500 mt-10 mb-0">
          Last updated {new Date(policy.lastUpdated).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      )}
    </>
  );
};

export default PolicyDocument;
